import { useState, useCallback, useEffect, useMemo } from 'react';
import { SitesPaginatedRequestBody } from 'api/sites/types';

interface UseSiteFiltersProps {
    localStorageKey: string;
}

export const useSiteFilters = ({ localStorageKey }: UseSiteFiltersProps) => {
    const [appliedFilters, setAppliedFilters] = useState<SitesPaginatedRequestBody>(() => {
        try {
            const saved = localStorage.getItem(localStorageKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Validate that all required fields are present
                const requiredFields = [
                    'customerSiteId', 'businessUnit', 'name', 'type',
                    'city', 'country', 'scope2region', 'status', 'userName'
                ];
                const hasAllFields = requiredFields.every(field => field in parsed);
                if (hasAllFields) {
                    return parsed;
                }
            }
        } catch (e) {
            console.error(`Failed to parse localStorage for key ${localStorageKey}:`, e);
        }

        // Return default empty filters if no valid saved state
        return {
            customerSiteId: null,
            businessUnit: null,
            name: null,
            type: null,
            city: null,
            country: null,
            scope2region: null,
            status: null,
            userName: null
        };
    });

    // Save to localStorage whenever appliedFilters changes
    const saveToLocalStorage = useMemo(
        () => (filters: SitesPaginatedRequestBody) => {
            try {
                localStorage.setItem(localStorageKey, JSON.stringify(filters));
            } catch (e) {
                console.error(`Failed to save to localStorage for key ${localStorageKey}:`, e);
            }
        },
        [localStorageKey]
    );

    useEffect(() => {
        saveToLocalStorage(appliedFilters);
    }, [appliedFilters, saveToLocalStorage]);

    const applyFilters = useCallback((columnFilters: any) => {
        // Start with current applied filters to preserve existing ones
        const newFilters: SitesPaginatedRequestBody = { ...appliedFilters };

        // Map column filters to request body, preserving existing filters
        columnFilters.forEach((filter: any) => {
            const { id, value } = filter;
            if (value && value !== '') {
                switch (id) {
                    case 'customerSiteId':
                        newFilters.customerSiteId = value;
                        break;
                    case 'name':
                        newFilters.name = value;
                        break;
                    case 'type':
                        newFilters.type = value;
                        break;
                    case 'businessUnit':
                        newFilters.businessUnit = value;
                        break;
                    case 'city':
                        newFilters.city = value;
                        break;
                    case 'country':
                        newFilters.country = value;
                        break;
                    case 'countryRegion':
                        newFilters.scope2region = value;
                        break;
                    case 'status':
                        newFilters.status = value;
                        break;
                    case 'firstName':
                        newFilters.userName = value;
                        break;
                    default:
                        // Ignore unknown filter types
                        break;
                }
            }
        });

        setAppliedFilters(newFilters);

        // Return the new filters so the component can clear columnFilters
        return newFilters;
    }, [appliedFilters]);

    const clearFilters = useCallback(() => {
        const emptyFilters = {
            customerSiteId: null,
            businessUnit: null,
            name: null,
            type: null,
            city: null,
            country: null,
            scope2region: null,
            status: null,
            userName: null
        };
        setAppliedFilters(emptyFilters);
    }, []);

    const clearSpecificFilter = useCallback((filterId: string) => {
        setAppliedFilters(prev => {
            const newFilters = { ...prev };
            switch (filterId) {
                case 'customerSiteId':
                    newFilters.customerSiteId = null;
                    break;
                case 'name':
                    newFilters.name = null;
                    break;
                case 'type':
                    newFilters.type = null;
                    break;
                case 'businessUnit':
                    newFilters.businessUnit = null;
                    break;
                case 'city':
                    newFilters.city = null;
                    break;
                case 'country':
                    newFilters.country = null;
                    break;
                case 'countryRegion':
                    newFilters.scope2region = null;
                    break;
                case 'status':
                    newFilters.status = null;
                    break;
                case 'firstName':
                    newFilters.userName = null;
                    break;
                default:
                    // Ignore unknown filter types
                    break;
            }
            return newFilters;
        });
    }, []);

    return {
        appliedFilters,
        applyFilters,
        clearFilters,
        clearSpecificFilter
    };
}; 